import React from 'react';
import { TableRow, TableCell, Typography, Card, Grid } from '@mui/material';
import {
  AccountStatusLabel,
  AccountStatusKeys,
  UserAccountsTableData,
  getAccountStatusIcon,
  formatAccountCount,
} from '../../../data/report/helpers/userAccountsUtils';
import ExpandIcon from '../../../components/ExpandIcon';
import RowDivider from './RowDivider';
import { Box } from '@mui/system';

type AccountStatusRowVariant = 'default' | 'condensed';

const condensedPaddingOffset = '32px';

interface AccountStatusRowProps {
  rowKey: AccountStatusKeys;
  columns: string[];
  data: UserAccountsTableData;
  subRows?: AccountStatusKeys[];
  subRowHeader?: string;
  isExpanded?: boolean;
  onToggleExpand?: () => void;
  variant?: AccountStatusRowVariant;
  showDivider?: boolean;
}

const AccountStatusRow: React.FC<AccountStatusRowProps> = ({
  rowKey,
  columns,
  data,
  subRows,
  subRowHeader,
  isExpanded = false,
  onToggleExpand,
  variant = 'default',
  showDivider,
}) => {
  const status = AccountStatusLabel[rowKey];
  const columnWidth = `${100 / (columns.length + 1)}%`;
  const isCondensed = variant === 'condensed';
  const cardColor = isCondensed ? 'tertiary' : 'primary';
  const expandable = subRows && subRows.length > 0;

  return (
    <>
      <Card
        color={cardColor}
        variant='ghost'
        sx={{
          ...(isCondensed && {
            mb: 0,
            borderRadius: 0,
          }),
        }}>
        <TableRow className='user-accounts'>
          <LabelCell
            label={status}
            icon={getAccountStatusIcon(status)}
            variant={variant}
            expandable={expandable}
            isExpanded={isExpanded}
            onToggleExpand={onToggleExpand}
          />
          <DataColumns columns={columns} data={data} rowKey={rowKey} />
        </TableRow>

        {expandable && isExpanded && (
          <>
            {isCondensed && <RowDivider inset={condensedPaddingOffset} />}
            <Box sx={{ mb: 2 }}>
              <Grid
                className='account-status-subrow-header'
                sx={{
                  ...(isCondensed && { paddingLeft: condensedPaddingOffset }),
                }}>
                <Typography variant='p16SemiBold' color='secondary'>
                  {subRowHeader}
                </Typography>
              </Grid>

              <Card className='account-status-subrow-card'>
                {subRows.map((subRowKey, index) => {
                  const label = AccountStatusLabel[subRowKey];
                  const isLastSubRow = index === subRows.length - 1;

                  return (
                    <React.Fragment key={subRowKey}>
                      <TableRow
                        className='account-status-subrow'
                        sx={{
                          ...(isCondensed && {
                            '& .MuiTableCell-root': {
                              paddingTop: '5px',
                              paddingBottom: '5px',
                            },
                          }),
                        }}>
                        <LabelCell
                          label={label}
                          icon={getAccountStatusIcon(label)}
                          width={columnWidth}
                          variant={variant}
                        />
                        <DataColumns
                          columns={columns}
                          data={data}
                          rowKey={subRowKey}
                          width={columnWidth}
                          isSubRow={true}
                        />
                      </TableRow>
                      {!isLastSubRow && !isCondensed && <RowDivider inset='48px' />}
                    </React.Fragment>
                  );
                })}
              </Card>
            </Box>
          </>
        )}
      </Card>

      {showDivider && isCondensed && <RowDivider inset={condensedPaddingOffset} />}
    </>
  );
};

const LabelCell: React.FC<{
  label: string;
  icon: React.ReactNode;
  width?: string;
  variant?: AccountStatusRowVariant;
  expandable?: boolean;
  isExpanded?: boolean;
  onToggleExpand?: () => void;
}> = ({ label, icon, width, variant = 'default', expandable, isExpanded, onToggleExpand }) => {
  const isCondensed = variant === 'condensed';

  return (
    <TableCell
      variant='body'
      className='account-status-label-cell'
      style={width ? { width } : undefined}>
      <Grid display='flex' alignItems='center' sx={{ height: '100%' }}>
        <Grid
          display='inherit'
          alignItems='inherit'
          className='account-status-label-icon'
          sx={{
            ...(isCondensed && { pl: condensedPaddingOffset }),
          }}>
          {icon}
        </Grid>
        <Typography variant='p16' color='secondary.main' className='account-status-label-text'>
          {label}
        </Typography>
        {expandable && onToggleExpand && (
          <Grid display='flex' alignItems='center' sx={{ height: 0, overflow: 'visible' }}>
            <Grid className='account-status-expand-icon'>
              <ExpandIcon isExpanded={isExpanded ?? false} onToggle={onToggleExpand} />
            </Grid>
          </Grid>
        )}
      </Grid>
    </TableCell>
  );
};

const DataColumns: React.FC<{
  columns: string[];
  data: UserAccountsTableData;
  rowKey: AccountStatusKeys;
  width?: string;
  isSubRow?: boolean;
}> = ({ columns, data, rowKey, width, isSubRow = false }) => (
  <>
    {columns.map(column => (
      <TableCell
        key={column}
        variant='body'
        className='account-status-data-cell'
        style={{
          ...(width && { width }),
        }}>
        <Typography
          variant='p18'
          color='secondary.main'
          fontWeight={column === 'Total' && !isSubRow ? 'bold' : 'inherit'}>
          {formatAccountCount(data[column]?.[rowKey] || 0)}
        </Typography>
      </TableCell>
    ))}
  </>
);

export default AccountStatusRow;
